import { toast as sonnerToast } from 'sonner';

/** How long a toast stays up before it dismisses itself, in ms. */
const DURATION = 4000;

/**
 * Hard ceiling on a toast's lifetime, independent of sonner's own timer.
 *
 * sonner (2.0.7) pauses every toast's dismiss timer while `document.hidden` is
 * true, and its `useIsDocumentHidden` hook subscribes with
 * `document.addEventListener('visibilitychange', …)` but unsubscribes with
 * `window.removeEventListener(…)` — a mismatched pair, so the listener is never
 * actually removed. On mobile the page goes hidden constantly (address bar,
 * app switch, screenshot, lock), and once that state sticks the toast's timer
 * stays paused and it NEVER closes: the "Не удалось сохранить продукт" banner
 * outlived the screen that raised it and followed the merchant across the app.
 *
 * There is no prop to opt out (`pauseWhenPageIsHidden` does not exist in this
 * version), so the facade owns the guarantee instead: whatever sonner's timer
 * is doing, we dismiss by id after the same duration. `toast.dismiss` on an
 * already-closed id is a no-op, so this is safe when the normal path works —
 * it only matters when the paused timer would otherwise never fire.
 */
function autoDismiss(id: string | number): string | number {
  setTimeout(() => sonnerToast.dismiss(id), DURATION);
  return id;
}

/**
 * Thin toast facade so features depend on `@noor/lib`, not `sonner` directly
 * (keeps the toast library swappable behind the shared boundary). Mirrors the
 * cpanel `success`/`error`/`warning`/`info` helpers.
 */
export const toast = {
  success: (message: string) =>
    autoDismiss(sonnerToast.success(message, { duration: DURATION })),
  error: (message: string) =>
    autoDismiss(sonnerToast.error(message, { duration: DURATION })),
  warning: (message: string) =>
    autoDismiss(sonnerToast.warning(message, { duration: DURATION })),
  info: (message: string) =>
    autoDismiss(sonnerToast.info(message, { duration: DURATION })),
  /**
   * A warning that stays up until it is dismissed — for an ongoing condition
   * rather than an event. Calling again with the same `id` updates it in
   * place. `onDismiss` fires when it closes, whether by its close button or
   * a programmatic `dismiss(id)`. (No auto-dismiss ceiling: it is meant to
   * outlive the default duration.)
   */
  sticky: (message: string, options: { id: string; onDismiss?: () => void }) =>
    sonnerToast.warning(message, {
      id: options.id,
      duration: Infinity,
      closeButton: true,
      ...(options.onDismiss ? { onDismiss: options.onDismiss } : {}),
    }),
  /** Close a toast early (or all of them when no id is given). */
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
};
